import { Audio, useCurrentFrame, getRemotionEnvironment, Video, useVideoConfig } from 'remotion';
import { useMemo, useCallback, useRef } from 'react';
import { calculateVolumeForFrame } from './utils/calculateAudioVolume';
import { AudioElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/AudioElement';

const AudioElementComponent: React.FC<{
  element: AudioElement;
}> = ({ element }) => {
  const { fromFrame, toFrame, details, trim } = element;

  const isPreview = !getRemotionEnvironment().isRendering;
  const durationInFrames = Math.round(toFrame - fromFrame);

  const frame = useCurrentFrame();
  const currentTrim = trim.find(t => t.fromFrame <= frame && t.toFrame >= frame);
  const { fps } = useVideoConfig();

  const frameRef = useRef(frame);
  frameRef.current = frame;

  const getVolumeForFrame = useCallback(() => {
    return calculateVolumeForFrame(frameRef.current, element, fps);
  }, [element, fps]);

  // Use Video component in preview mode to prevent audio loss when tracks are recreated
  // Use Audio component only during rendering as required by Remotion
  const AudioComp = useMemo(() => {
    if (!isPreview) {
      return Audio;
    }
    return Video;
  }, [isPreview]);

  const handleError = useCallback(
    (error: Error | unknown) => {
      window.logger.error('Audio playback error in AudioElementComponent', {
        src: details.src,
        error: error instanceof Error ? error.message : String(error)
      });
    },
    [details.src]
  );

  if (!currentTrim && trim.length !== 0) {
    return null;
  }

  return (
    <AudioComp
      className="hidden"
      src={details.src}
      pauseWhenBuffering={false}
      startFrom={0}
      volume={getVolumeForFrame}
      onError={handleError}
      {...(details.loop ? { loop: true } : { endAt: durationInFrames })}
    />
  );
};

export default AudioElementComponent;
